import type {
  QueryParams,
  QueryParamsWithDefaults,
  DefaultQueryParams,
} from '@/modules/common/context/QueryParams/models';
import type {QueryParamsSchema} from '@/modules/common/context/QueryParams/schema';

import {addDays, formatISO, isBefore} from 'date-fns';
import {uniq} from 'lodash';

import {parseWithSchema} from '@/modules/common/context/QueryParams/utils/parseWithSchema';
import {Room} from '@/modules/common/models/room';
import {Rooms} from '@/modules/common/models/rooms';

type Options = {
  schema: QueryParamsSchema;
  urlSearchParams: URLSearchParams;
  getDefaultValues: (
    travelType: QueryParams['travelType']
  ) => DefaultQueryParams;
  limits: {
    maxAdults: number;
    maxChildren: number;
    maxTravellers: number;
    maxRooms: number;
  };
};

export const parseQueryParams = ({
  schema,
  urlSearchParams,
  getDefaultValues,
  limits,
}: Options): QueryParamsWithDefaults => {
  const queryParams = parseWithSchema({schema, urlSearchParams});
  const defaultValues = getDefaultValues(queryParams.travelType);

  const queryParamsWithDefaults: QueryParamsWithDefaults = {
    ...queryParams,
    dateMin: queryParams.dateMin ?? defaultValues.dateMin,
    dateMax: queryParams.dateMax ?? defaultValues.dateMax,
    airports:
      queryParams.airports !== null &&
      queryParams.airports.length > 0 &&
      !queryParams.airports.includes('DEFAULT') // either all GIATA or DEFAULT, not both
        ? queryParams.airports
        : defaultValues.airports,
    adults:
      queryParams.adults !== null && queryParams.adults <= limits.maxAdults
        ? queryParams.adults
        : defaultValues.adults,
    children:
      queryParams.children !== null &&
      queryParams.children.length <= limits.maxChildren
        ? queryParams.children
        : defaultValues.children,
    rooms:
      queryParams.rooms !== null &&
      queryParams.rooms.length <= limits.maxRooms &&
      queryParams.rooms.adultsCount <= limits.maxAdults &&
      queryParams.rooms.childrenCount <= limits.maxChildren &&
      queryParams.rooms.travellerCount <= limits.maxTravellers
        ? queryParams.rooms
        : defaultValues.rooms,
    durationMin: queryParams.durationMin ?? defaultValues.durationMin,
    durationMax: queryParams.durationMax ?? defaultValues.durationMax,
    travelType: queryParams.travelType ?? defaultValues.travelType,
    sortBy: queryParams.sortBy ?? defaultValues.sortBy,
  };

  if (
    queryParams.adults !== null &&
    queryParams.children !== null &&
    queryParams.adults + queryParams.children.length > limits.maxTravellers
  ) {
    queryParamsWithDefaults.adults = defaultValues.adults;
    queryParamsWithDefaults.children = defaultValues.children;
  }

  // convert legacy "adults" and "children" params into "rooms"
  if (queryParams.rooms === null || queryParams.rooms.length === 0) {
    queryParamsWithDefaults.rooms = Rooms.create(
      Room.create({
        adultsCount: queryParamsWithDefaults.adults,
        childrenAges: queryParamsWithDefaults.children,
      })
    );
  }

  if (
    queryParams.durationMin === null ||
    queryParams.durationMax === null ||
    // durationMax can't be less then durationMin (but they can be equal)
    queryParams.durationMax < queryParams.durationMin
  ) {
    queryParamsWithDefaults.durationMin = defaultValues.durationMin;
    queryParamsWithDefaults.durationMax = defaultValues.durationMax;
  }

  if (
    queryParams.dateMin === null ||
    queryParams.dateMax === null ||
    // there must be at least one day between
    queryParams.dateMin === queryParams.dateMax ||
    // dateMax can't be before dateMin
    queryParams.dateMax < queryParams.dateMin
  ) {
    queryParamsWithDefaults.dateMin = defaultValues.dateMin;
    queryParamsWithDefaults.dateMax = defaultValues.dateMax;
    // when the dates are invalid, the durations are aswell
    queryParamsWithDefaults.durationMin = defaultValues.durationMin;
    queryParamsWithDefaults.durationMax = defaultValues.durationMax;
  }

  if (queryParams.dateMin !== null && queryParams.dateMax !== null) {
    const today = formatISO(Date.now(), {representation: 'date'});
    const tomorrow = formatISO(addDays(Date.now(), 1), {
      representation: 'date',
    });

    /**
     * Try to shift dates that are in the past to avoid no offer situations.
     * @see https://invia.atlassian.net/browse/FFP-3168
     */
    if (queryParams.dateMin < today && queryParams.dateMax < today) {
      queryParamsWithDefaults.dateMin = defaultValues.dateMin;
      queryParamsWithDefaults.dateMax = defaultValues.dateMax;
      // exact
      if (queryParams.durationMin === 0 && queryParams.durationMax === 0) {
        queryParamsWithDefaults.durationMin = defaultValues.durationMin;
        queryParamsWithDefaults.durationMax = defaultValues.durationMax;
      }
    } else if (
      queryParams.dateMin < tomorrow &&
      queryParams.dateMax > today &&
      queryParams.durationMax !== null
    ) {
      const shiftedDateMax = addDays(tomorrow, queryParams.durationMax);
      queryParamsWithDefaults.dateMin = formatISO(tomorrow, {
        representation: 'date',
      });
      if (isBefore(queryParams.dateMax, shiftedDateMax)) {
        queryParamsWithDefaults.dateMax = formatISO(shiftedDateMax, {
          representation: 'date',
        });
      }
    }
  }

  if (queryParams.roomViews !== null) {
    queryParamsWithDefaults.roomAttributes =
      queryParams.roomAttributes === null
        ? queryParams.roomViews
        : uniq([...queryParams.roomViews, ...queryParams.roomAttributes]);
    queryParamsWithDefaults.roomViews = [];
  }

  if (queryParamsWithDefaults.roomAttributes !== null) {
    queryParamsWithDefaults.roomAttributes = uniq(
      queryParamsWithDefaults.roomAttributes
    );
  }

  return queryParamsWithDefaults;
};
